import styled from "styled-components";
import { useState, useEffect, useRef } from "react";
import { FaChevronLeft, FaChevronRight, FaBrain } from "react-icons/fa";

/* ─────────────────────────────────────────
   IMAGE SLOTS
───────────────────────────────────────── */
const expertAIImage = require("../assets/images/summit/bodhanai.webp");
const sanjayKumarImage = require("../assets/images/summit/ShriSanjayKumarIAS.webp");

const valedictoryCarouselImages = [
  require("../assets/images/summit/socialimpact1.webp"),
  require("../assets/images/summit/socialimpact2.webp"),
  require("../assets/images/summit/socialimpact3.webp"),
  require("../assets/images/summit/socialimpact4.webp"),
];

const awardWinners = [
  {
    org: "Indian Oil Corporation Limited",
    project: "Solar Eri Spinning – Women-Led Sustainable Livelihood Initiative",
    desc: "Empowering over 2,500 women artisans in Meghalaya through clean energy-powered Eri silk spinning, sustainable livelihoods, and digital market access.",
  },
  {
    org: "Ford Business Solutions India",
    project: "Go Further in Saving Lives - Building a Culture of Safety",
    desc: "Transforming citizens into trained first responders through technology-enabled emergency awareness and community safety training initiatives.",
  },
  {
    org: "Tanla Platforms Limited",
    project: "Cyberabad Traffic Pulse",
    desc: "Leveraging real-time technology and verified alerts to improve urban mobility, reduce congestion, and enhance road safety for citizens.",
  },
  {
    org: "Altius Telecom",
    project: "Technology-enabled Digital Dispensaries",
    desc: "Expanding access to quality healthcare in underserved regions through telemedicine-enabled digital healthcare infrastructure.",
  },
];

/* ─────────────────────────────────────────
   CAROUSEL COMPONENT
───────────────────────────────────────── */
const Carousel = ({ images }) => {
  const [current, setCurrent] = useState(0);
  const timerRef = useRef(null);
  const total = images.length;

  const prev = () => setCurrent((c) => (c - 1 + total) % total);
  const next = () => setCurrent((c) => (c + 1) % total);

  useEffect(() => {
    if (total < 2) return;
    timerRef.current = setInterval(() => setCurrent((c) => (c + 1) % total), 3500);
    return () => clearInterval(timerRef.current);
  }, [total]);

  return (
    <CarouselWrap>
      <CarouselTrack>
        {total > 0 ? (
          <CarouselImg src={images[current]} alt={`Valedictory ${current + 1}`} />
        ) : (
          <CarouselPlaceholder>
            <PlaceholderText>Images coming soon</PlaceholderText>
          </CarouselPlaceholder>
        )}
      </CarouselTrack>
      <CarouselControls>
        <CtrlBtn onClick={prev}><FaChevronLeft /></CtrlBtn>
        <Dots>
          {total > 0
            ? images.map((_, i) => <Dot key={i} active={i === current} onClick={() => setCurrent(i)} />)
            : Array.from({ length: 5 }).map((_, i) => <Dot key={i} active={i === 0} />)
          }
        </Dots>
        <CtrlBtn onClick={next}><FaChevronRight /></CtrlBtn>
      </CarouselControls>
    </CarouselWrap>
  );
};

const ValedictorySession = () => {
  return (
    <Section id="valedictory-session">
      <Wrapper>

        <SectionLabel>Closing Ceremony</SectionLabel>
        <MainHeading>Valedictory Session</MainHeading>
        <Divider />

        <ExpertCard>
          <ExpertInfo>
            <ExpertTag><FaBrain /> Keynote Speech</ExpertTag>
            <ExpertName>Shri Sanjay Kumar IAS</ExpertName>
            <ExpertLinks>
              <ExpertLink>Secretary, School Education &amp; Literacy, MoE, Govt. of India</ExpertLink>
            </ExpertLinks>
          </ExpertInfo>
          <ExpertImageWrap>
            <img src={sanjayKumarImage} alt="Shri Sanjay Kumar IAS" />
          </ExpertImageWrap>
        </ExpertCard>

        <Card>
          <ContentCol>
            {awardWinners.map((a, i) => (
              <AwardCard key={i}>
                <AwardOrg>{a.org}</AwardOrg>
                <AwardProject>{a.project}</AwardProject>
                <AwardDesc>{a.desc}</AwardDesc>
              </AwardCard>
            ))}
          </ContentCol>

          <CarouselCol>
            <Carousel images={valedictoryCarouselImages} />
          </CarouselCol>
        </Card>
      </Wrapper>
    </Section>
  );
};

export default ValedictorySession;


/* ═══════════════════════════════════════
   STYLES
═══════════════════════════════════════ */

const Section = styled.section`
  padding: 20px 20px;
  background: #faf8f5;

  @media (max-width: 768px) {
    padding: 36px 14px;
  }
`;

const Wrapper = styled.div`
  max-width: 1100px;
  margin: 0 auto;
`;

const SectionLabel = styled.p`
  text-align: center;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 3px;
  text-transform: uppercase;
  color: #d6a54f;
  margin-bottom: 10px;
`;

const MainHeading = styled.h2`
  text-align: center;
  font-size: 38px;
  font-weight: 700;
  color: #791e1b;
  margin: 0 0 16px;
  line-height: 1.3;

  @media (max-width: 768px) {
    font-size: 22px;
  }
`;

const Divider = styled.div`
  width: 60px;
  height: 4px;
  background: #d6a54f;
  border-radius: 2px;
  margin: 0 auto 48px;
`;

const Card = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 6px 30px rgba(0, 0, 0, 0.09);
  border: 1px solid #ede8e0;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ContentCol = styled.div`
  background: #ffffff;
  padding: 24px 28px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  border-right: 1px solid #ede8e0;
  overflow-y: auto;

  @media (max-width: 768px) {
    padding: 20px 18px;
    border-right: none;
    border-bottom: 1px solid #ede8e0;
  }
`;

const AwardCard = styled.div`
  background: #faf8f5;
  border-radius: 12px;
  padding: 16px 18px;
  border-left: 4px solid #d6a54f;
  display: flex;
  flex-direction: column;
  gap: 6px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
`;

const AwardOrg = styled.span`
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  color: #791e1b;
`;

const AwardProject = styled.h4`
  font-size: 14px;
  font-weight: 700;
  color: #171f32;
  margin: 0;
  line-height: 1.4;
`;

const AwardDesc = styled.p`
  font-size: 13px;
  color: #6b7280;
  line-height: 1.6;
  margin: 0;
`;

const CarouselCol = styled.div`
  background: #171f32;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 28px 20px;
`;

const CarouselWrap = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
`;

const CarouselTrack = styled.div`
  width: 100%;
  height: 260px;
  overflow: hidden;
`;

const CarouselImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const CarouselPlaceholder = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 260px;
  background: repeating-linear-gradient(
    135deg,
    #1e2740 0px,
    #1e2740 18px,
    #171f32 18px,
    #171f32 36px
  );
`;

const PlaceholderText = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: #d6a54f;
  letter-spacing: 1px;
  background: rgba(0, 0, 0, 0.35);
  padding: 6px 16px;
  border-radius: 20px;
`;

const CarouselControls = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 12px 16px;
  background: #171f32;
`;

const CtrlBtn = styled.button`
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.4);
  color: #ffffff;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 12px;
  transition: background 0.2s;

  &:hover {
    background: rgba(255, 255, 255, 0.25);
  }
`;

const Dots = styled.div`
  display: flex;
  gap: 6px;
`;

const Dot = styled.button`
  width: ${(p) => (p.active ? "20px" : "8px")};
  height: 8px;
  border-radius: 4px;
  border: none;
  background: ${(p) => (p.active ? "#ffffff" : "rgba(255,255,255,0.3)")};
  cursor: pointer;
  transition: width 0.3s ease, background 0.3s ease;
  padding: 0;
`;

const Description = styled.p`
  font-size: 15px;
  line-height: 1.9;
  color: #4b5563;
  margin: 0;
`;

const KeynoteBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  border-left: 4px solid #791e1b;
  padding-left: 20px;
`;

const KeynoteTag = styled.span`
  display: flex;
  align-items: center;
  gap: 7px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 2px;
  text-transform: uppercase;
  color: #d6a54f;
`;

const SpeakerName = styled.h3`
  font-size: 22px;
  font-weight: 700;
  color: #171f32;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 18px;
  }
`;

const SpeakerRole = styled.p`
  font-size: 14px;
  color: #6b7280;
  margin: 0;
  line-height: 1.6;
`;

/* ── Expert Talk on AI ── */

const ExpertCard = styled.div`
  background: #171f32;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 4px 22px rgba(0, 0, 0, 0.13);
  display: grid;
  grid-template-columns: 1fr 1fr;
  margin-bottom: 24px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ExpertInfo = styled.div`
  padding: 36px 40px 28px;
  display: flex;
  flex-direction: column;
  gap: 10px;

  @media (max-width: 768px) {
    padding: 24px 20px 20px;
  }
`;

const ExpertTag = styled.span`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 2px;
  text-transform: uppercase;
  color: #d6a54f;
`;

const ExpertName = styled.h3`
  font-size: 26px;
  font-weight: 700;
  color: #fff;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 20px;
  }
`;

const ExpertLinks = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 4px;
`;

const ExpertLink = styled.span`
  background: rgba(214, 165, 79, 0.15);
  border: 1px solid rgba(214, 165, 79, 0.4);
  color: #d6a54f;
  font-size: 12px;
  font-weight: 600;
  padding: 4px 14px;
  border-radius: 20px;
  text-decoration: none;
`;

const ExpertImageWrap = styled.div`
  min-height: 260px;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`;
